import React, { useState, useEffect, useCallback, useId } from 'react';
import { AlertCircle } from 'lucide-react';
import { getThread, replyToThread } from '../../api/threads';
import { sanitizeThreadContent, sanitizeName, validateAndSanitizeInput } from '../../utils/sanitize';
import { usePolling } from '../../hooks/usePolling';
import { useI18n } from '../../i18n/I18nContext';
import { formatDateTime, formatRelative } from '../../utils/datetime';
import { errorText } from '../../utils/errorText';
import { useHideBottomNav, useMediaQuery } from '../Layout/shellState';
import PageHead from '../PageHead/PageHead';
import Avatar from '../Avatar/Avatar';
import StatusBadge from '../StatusBadge/StatusBadge';
import InlineEmpty from '../InlineEmpty/InlineEmpty';
import './ThreadDetail.css';

// An ISO stamp for <time dateTime>, or nothing for a value that is not a date
function isoOf(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

const STATUS_TONE = { ANSWERED: 'success', OPEN: 'warning', CLOSED: 'neutral' };
const STATUS_LABEL_KEY = { ANSWERED: 'qaThreads.statusAnswered', OPEN: 'qaThreads.statusUnanswered', CLOSED: 'qaThreads.statusClosed' };

// Note: classnames here are namespaced with a `qtd-` prefix (QA Thread Detail)
// — see the note in ThreadList.js. TeacherQA.css (a different, unrelated
// page) already defines generic names like `.question-card`/`.reply-card`,
// and CRA bundles every page's CSS into one global stylesheet.

/**
 * One lesson question with its answers and the box to answer it, drawn as the page it is (a back
 * button, the course over the title, cards): CourseQA shows it in place of the list. A phone gets
 * the answer box pinned to the bottom edge while the tab bar steps aside.
 *
 * Props: thread (the list row; the full thread is fetched), courseId, courseTitle (over the
 * heading), lessonName, onBack.
 */
function ThreadDetail({ thread, courseId, courseTitle, lessonName, onBack }) {
  const { t, lang } = useI18n();
  const textareaId = useId();
  const phone = useMediaQuery('(max-width: 639px)', false);
  useHideBottomNav();

  const [threadData, setThreadData] = useState(thread);
  const [replies, setReplies] = useState(thread.replies || []);
  const [error, setError] = useState(null);
  // The latest thread could not be read: { detail } carries the API client's own sentence, if any
  const [loadError, setLoadError] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [saving, setSaving] = useState(false);

  const lessonId = thread.lessonId || thread.lesson?.id;

  const fetchThreadDetail = useCallback(async ({ silent = false } = {}) => {
    try {
      const data = await getThread(courseId, lessonId, thread.id);
      setThreadData(data);
      setReplies(data.replies || []);
      setLoadError(null);
    } catch (err) {
      // Surface it: without this, a failed initial load (or a thread
      // deleted server-side) silently kept showing the list-row `thread`
      // prop forever, which lacks the `replies` array the list endpoint
      // never returns — the reader would never know the data was stale.
      if (!silent) {
        setLoadError({ detail: errorText(err, '') });
      }
    }
  }, [courseId, lessonId, thread.id]);

  useEffect(() => {
    fetchThreadDetail();
  }, [fetchThreadDetail]);

  // Polling — paused while tab hidden, refetches on visibility regain.
  // Silent: a transient hiccup on a background refresh shouldn't blow away
  // an already-successfully-displayed thread with an error banner.
  usePolling(() => fetchThreadDetail({ silent: true }), 30_000);

  const handleReply = async (e) => {
    e.preventDefault();

    // SECURITY: Validate and sanitize reply content
    const validation = validateAndSanitizeInput(replyContent, { minLength: 1, maxLength: 2000 });
    if (!validation.valid) {
      // validation.error is English text from utils/sanitize: the page says it in its own words
      setError(t('qaThreads.failedToPostReply'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await replyToThread(courseId, lessonId, thread.id, {
        body: validation.sanitized,
      });

      setReplyContent('');
      await fetchThreadDetail();
    } catch (err) {
      setError(errorText(err, t('qaThreads.failedToPostReply')));
    } finally {
      setSaving(false);
    }
  };

  const status = threadData.status || 'OPEN';
  // SECURITY: Sanitize author names to prevent XSS
  const asker = sanitizeName(threadData.authorName || t('qaThreads.anonymous'));
  const asked = threadData.askedAt || threadData.createdAt;

  return (
    <div className="qtd-root">
      <PageHead
        back={{ onClick: onBack, label: t('qaThreads.backToQA') }}
        eyebrow={courseTitle || t('qaThreads.parent')}
        title={t('qaThreads.pageTitle')}
        actions={(
          <>
            <StatusBadge tone={STATUS_TONE[status] || 'warning'}>
              {t(STATUS_LABEL_KEY[status] || STATUS_LABEL_KEY.OPEN)}
            </StatusBadge>
            <span className="ds-badge ds-badge-success ds-badge--dot" title={t('qaThreads.updatesEvery30Seconds')}>
              {t('qaThreads.liveBadge')}
              <span className="visually-hidden">: {t('qaThreads.updatesEvery30Seconds')}</span>
            </span>
          </>
        )}
      />

      {loadError && (
        <div className="ui-alert qtd-alert" role="alert">
          <AlertCircle aria-hidden="true" />
          <p>{[t('qaThreads.failedToLoadThread'), loadError.detail].filter(Boolean).join(' ')}</p>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => fetchThreadDetail()}>
            {t('qaThreads.retry')}
          </button>
        </div>
      )}

      {/* The question */}
      <section className="section-card qtd-question" aria-label={t('qaThreads.sectionQuestion')}>
        {lessonName && <h2 className="qtd-question-title">{lessonName}</h2>}
        {/* SECURITY: Sanitize thread content to prevent XSS */}
        <p className="qtd-question-content">{sanitizeThreadContent(threadData.body)}</p>
        <div className="qtd-asker">
          <Avatar name={asker} id={threadData.authorId || thread.id} size="sm" decorative />
          <span className="qtd-asker__name">{asker}</span>
          {asked && (
            <>
              <span aria-hidden="true">·</span>
              <time dateTime={isoOf(asked)} title={formatDateTime(asked, { lang, year: 'always' })}>
                {formatRelative(asked, { lang })}
              </time>
            </>
          )}
        </div>
      </section>

      {/* The answers */}
      <section className="section-card qtd-replies">
        <div className="section-head">
          <h2 className="section-head__title">{t('qaThreads.repliesSectionTitle', { count: replies.length })}</h2>
        </div>

        {replies.length === 0 ? (
          <InlineEmpty kind="messages" title={t('qaThreads.noRepliesYet')} />
        ) : (
          <ul className="qtd-replies-list">
            {replies.map((reply) => {
              const author = sanitizeName(reply.authorName || t('qaThreads.anonymous'));
              return (
                <li key={reply.id} className={`qtd-reply${reply.isTeacherReply ? ' is-teacher' : ''}`}>
                  <Avatar name={author} id={reply.authorId || reply.id} size="sm" decorative />
                  <div className="qtd-reply__bubble">
                    <div className="qtd-reply__head">
                      {/* SECURITY: Sanitize reply author name to prevent XSS */}
                      <strong>{author}</strong>
                      {reply.isTeacherReply && (
                        <span className="ds-badge ds-badge-info">{t('qaThreads.teacherBadge')}</span>
                      )}
                      {reply.createdAt && (
                        <time
                          className="qtd-reply__time"
                          dateTime={isoOf(reply.createdAt)}
                          title={formatDateTime(reply.createdAt, { lang, year: 'always' })}
                        >
                          {formatRelative(reply.createdAt, { lang })}
                        </time>
                      )}
                    </div>
                    {/* SECURITY: Sanitize reply content to prevent XSS */}
                    <p>{sanitizeThreadContent(reply.body)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* The answer box */}
      <form
        className={`qtd-reply-form ${phone ? 'ui-sticky-actions qtd-composer' : 'section-card'}`}
        onSubmit={handleReply}
      >
        <div className="ui-field">
          <label className={`ui-label${phone ? ' visually-hidden' : ''}`} htmlFor={textareaId}>
            {t('qaThreads.postAReply')}
          </label>
          <textarea
            id={textareaId}
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder={t('qaThreads.replyPlaceholder')}
            maxLength="2000"
            rows={phone ? 2 : 5}
          />
          {!phone && (
            <p className="ui-hint qtd-char-count">{t('qaThreads.charCount', { count: replyContent.length, max: 2000 })}</p>
          )}
        </div>

        {error && (
          <div className="ui-alert qtd-alert" role="alert">
            <AlertCircle aria-hidden="true" />
            <p>{error}</p>
          </div>
        )}

        <div className="qtd-actions">
          {/* On a phone the back button above already leaves the thread */}
          {!phone && (
            <button type="button" onClick={onBack} className="ds-btn ds-btn-secondary">
              {t('qaThreads.cancel')}
            </button>
          )}
          <button
            type="submit"
            disabled={saving || !replyContent.trim()}
            className={`ds-btn ds-btn-primary${saving ? ' is-loading' : ''}`}
            aria-busy={saving || undefined}
          >
            {t('qaThreads.postReply')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ThreadDetail;
